/* oxlint-disable no-throw-literal */
import qs from 'qs'
import request from 'api/requestUtils'
import config from 'constants/config'
import type { operations as SearchOperations, definitions as SearchDefinitions } from '@luxuryescapes/contract-search'
import {
  cruiseDealMap,
  cruiseLineMap,
  cruiseOfferMap,
  cruiseDepartureByIdMap,
  cruiseBookingRateListMap,
  cruiseVendorConfigurationMap,
  cruiseBookingCabinListMap,
  cruiseBookingCabinSelectionMap,
  cruiseBookingCabinPricingMap,
  cruiseBookingRateDetailsListMap,
  cruiseBookingCabinDetailsListMap,
  cruiseShipMap,
  cruiseOfferSnapshotMap,
  cruiseQuickModificationBookingDisplayMap,
  cruiseDealsMap,
  mapCruiseOfferMemberPricing,
} from './mappers/Cruise/cruiseMap'

import { cruiseOfferSummaryMap } from './mappers/Cruise/cruiseOfferSummaryMap'

import type { CruisesContract, definitions } from '@luxuryescapes/contract-svc-cruise'
import { offerHasValidDepartures, setCruiseVendorBookingTimer } from 'lib/cruises/cruiseUtils'
import { split } from 'lib/array/arrayUtils'
import { isUuidV4 } from 'lib/string/uuidV4Utils'
import {
  CRUISE_DEALS_PAGE_SIZE,
  CRUISE_LINE_CUSTOM_INFO,
  CruiseTravellerSearchType,
  CruiseTravellerType,
} from 'constants/cruise'

// Types from contract-search for cruise search endpoints
type CruiseSearchListPostBody =
  SearchOperations['/api/search/cruise/v1/list/post']['requestBody']['content']['application/json']
type CruiseSearchListPostResponse =
  SearchOperations['/api/search/cruise/v1/list/post']['responses'][200]['content']['application/json']
type CruiseSearchRegion = SearchDefinitions['region']
type CruiseSearchFacetListPostBody =
  SearchOperations['/api/search/cruise/v1/facet/list/post']['requestBody']['content']['application/json']
type CruiseSearchFacetListPostResponse =
  SearchOperations['/api/search/cruise/v1/facet/list/post']['responses'][200]['content']['application/json']
// Remove these extensions once contract-search includes the CRUZ-3686 cruise filters.
type CruiseSearchRequestWithTravellerTypes = CruiseSearchListPostBody & {
  travellerTypes?: Array<string>
}
type CruiseSearchFacetListPostBodyWithTravellerTypes =
  Omit<CruiseSearchFacetListPostBody, 'facetTypes'> & {
    facetTypes: Array<App.CruiseSearchFacetType>
    travellerTypes?: Array<string>
  }
type CruiseSearchFacetListPostResponseWithTravellerTypes =
  CruiseSearchFacetListPostResponse & {
    filtersV2: CruiseSearchFacetListPostResponse['filtersV2'] & {
      traveller_types: Array<App.CruiseSearchFacetV2Item>
    }
  }

const CRUISE_TRAVELLER_SEARCH_TYPE_BY_FILTER: Record<CruiseTravellerType, CruiseTravellerSearchType> = {
  [CruiseTravellerType.ROMANTIC]: CruiseTravellerSearchType.ROMANTIC,
  [CruiseTravellerType.FAMILY]: CruiseTravellerSearchType.FAMILY,
  [CruiseTravellerType.SINGLES]: CruiseTravellerSearchType.SINGLE,
}

const CRUISE_TRAVELLER_FILTER_BY_SEARCH_TYPE: Record<CruiseTravellerSearchType, CruiseTravellerType> = {
  [CruiseTravellerSearchType.ROMANTIC]: CruiseTravellerType.ROMANTIC,
  [CruiseTravellerSearchType.FAMILY]: CruiseTravellerType.FAMILY,
  [CruiseTravellerSearchType.SINGLE]: CruiseTravellerType.SINGLES,
}

type CruisePastPassengerLookupPassenger = {
  paxIndex: string
  firstName: string
  lastName: string
  dateOfBirth: string
  middleName?: string
  state?: string
  postalCode?: string
  phoneNumber?: string
  emailAddress?: string
}

export namespace CruiseAPI {
  type ContractCruiseRateListBody = definitions['RATE_LIST_BODY']
  export type CruiseRateListBody = Omit<ContractCruiseRateListBody, 'travelers'> & {
    travelers: Omit<
      ContractCruiseRateListBody['travelers'],
      'pastPassengerFirstNames' | 'pastPassengerLastNames'
    > & {
      passengersInfo?: Array<App.PastPassengerInfo>
    }
  }
  export type CruiseRateListResponse = definitions['RATE_LIST_RESPONSE']
  export type CruiseCabinRate = definitions['CABIN_RATE']
  export type CruiseCabinRatePackage = definitions['CABIN_RATE_PACKAGE']

  export type CruiseVendorConfigurationBody = {
    departureId: string
    region: App.Cruises.Region
  }

  export type CruiseVendorConfigurationResponse = App.Cruises.CruiseVendorConfiguration

  export type CruiseCabinListBody = definitions['CABIN_LIST_BODY']
  export type CruiseCabinListResponse = definitions['CABIN_LIST_RESPONSE']

  export type CruiseRateDetailsListBody = definitions['RATE_CODE_DETAILS_LIST_BODY']
  export type CruiseRateDetailsListResponse = definitions['RATE_CODE_DETAILS_LIST_RESPONSE']

  export type CruiseCabinSelectionBody = definitions['CABIN_SELECTION_BODY']
  export type CruiseCabinSelectionResponse = definitions['CABIN_SELECTION_RESPONSE']

  export type CruiseCabinPricingBody = definitions['CABIN_PRICING_BODY']
  export type CruiseCabinPricingResponse = definitions['CABIN_PRICING_RESPONSE']

  export type CruisePastPassengerLookupBody = {
    departureId: string
    region: App.Cruises.Region
    sessionId?: string
    pastPassenger: CruisePastPassengerLookupPassenger
  }

  export type CruisePastPassengerLookupResponse = {
    pastPassengers: Array<App.Cruises.PastPassengerLookupResult>
  }

  export type CruiseCabinDetailsListBody = definitions['CABIN_DETAILS_LIST_BODY']
  export type CruiseCabinDetailsListResponse = definitions['CABIN_DETAILS_LIST_RESPONSE']

  export type CruiseCabinReleaseBody = definitions['CABIN_RELEASE_BODY']
  export type CruiseCabinReleaseRelease = definitions['CABIN_RELEASE_RESPONSE']

  export type CruiseShipResponse = definitions['SHIP']

  export type CruiseConsolidatedPaymentSchedule = definitions['GET_CONSOLIDATED_PAYMENT_SCHEDULE']

  export type CruiseInclusion = definitions['INCLUSION']

  export type CruiseBooking = definitions['BOOKING_BY_ID']
  export type CruiseOfferSnapshot = definitions['OFFER_SNAPSHOT']
  export type CruiseQuickModificationDisplay = definitions['QUICK_MODIFICATION_BOOKING_DISPLAY']
  export type CruiseItinerarySnapshot = definitions['ITINERARY_SNAPSHOT']

  export type CruiseDealBase = definitions['DEAL_RETRIEVAL_BASE']
  export type CruiseDeal = definitions['DEAL_RETRIEVAL']

  export type DepartureByIdResponse = definitions['DEPARTURE']
  export type MemberPriceResponse = definitions['MEMBER_PRICE_RESPONSE']
  export type CruiseOfferMemberPricingResponse = MemberPriceResponse

  export type OfferInclusion = definitions['OFFER_INCLUSION']
  export type DatedOfferInclusion = definitions['DATED_OFFER_INCLUSION']
}

const BASE_URI = '/api/cruise/v2'

const DEFAULT_CABIN_HOLD_TIMEOUT = 900 // 15 minutes
const MAX_NUM_OF_PARALLEL_REQUESTS = 3

export async function getCruiseDealBySlug(slug: string) {
  const response = await request.get<App.ApiResponse<CruiseAPI.CruiseDeal>>(`${BASE_URI}/deals/${slug}`)
  return cruiseDealMap(response.result)
}

export async function getCruiseDeals(
  region: string,
  isActive: boolean = true,
  skip: number = 0,
  take: number = CRUISE_DEALS_PAGE_SIZE,
): Promise<{ deals: Array<App.Cruises.CruiseDealBase>, total: number }> {
  const params = qs.stringify({
    isActive,
    region,
    skip,
    take,
  })
  const response = await request.get<App.ApiResponse<Array<CruiseAPI.CruiseDealBase>>>(`${BASE_URI}/deals?${params}`)
  return {
    deals: cruiseDealsMap(response.result),
    total: response.total ?? 0,
  }
}

export async function getCruiseLineByPageSlug(pageSlug: string) {
  const response = await request.get<App.ApiResponse<CruisesContract.CruiseLine>>(`${BASE_URI}/vendors/slug/${pageSlug}`)
  return cruiseLineMap(response.result)
}

export async function getCruiseSearchFacets(params: App.CruiseSearchFacetParams, region?: string): Promise<App.CruiseSearchFacetsV2> {
  const destinationIds = params.destinationIds?.flatMap(id => id.split(','))
  const departurePlaceIds = params.departureIds?.flatMap(id => id.split(','))
  const departureIds = params.offerDepartureIds?.flatMap(id => id.split(','))
  const cruiseShips = params.cruiseShips?.flatMap(name => name.split(','))
  const durationRange = params.durationRange?.flatMap(duration => duration.split(','))
  const campaigns = params.campaigns?.flatMap(campaign => campaign.split(','))
  const cruiseTypes = params.cruiseTypes?.flatMap(cruiseType => cruiseType.split(','))
  const travellerTypes = params.travellerTypes?.flatMap(travellerType => travellerType.split(','))
  const cruiseTravellerSearchTypes = getCruiseTravellerSearchTypes(travellerTypes)
  const { durationMin, durationMax } = params

  const body = {
    brand: config.BRAND,
    facetTypes: params.facetTypes,
    departureIds,
    destinationPlaceIds: destinationIds,
    departurePlaceIds,
    arrivalPlaceId: params.destinationId,
    departurePlaceId: params.departurePlaceId,
    cruiseLines: params.cruiseLines,
    departureDate: params.departureDate?.substring(0, 7),
    departureMonths: params.departureMonths?.filter(Boolean),
    departureStartDate: params.departureStartDate,
    departureEndDate: params.departureEndDate,
    rateCodes: params.rateCodes,
    includedFixedFilters: true,
    campaigns,
    cabinTypes: params.cabinTypes,
    minPrice: params.minPrice || undefined,
    maxPrice: params.maxPrice || undefined,
    ...(durationMin && { durationMin }),
    ...(durationMax && { durationMax }),
    ...(durationRange && { durationRange }),
    ...(region && { region: region as CruiseSearchRegion }),
    ...(params.isSpecialOffers && { isSpecialOffers: params.isSpecialOffers }),
    ...(params.isLuxExclusivePromotion && { isLuxExclusivePromotion: params.isLuxExclusivePromotion }),
    ...(params.isLuxPlus && { isLuxPlus: params.isLuxPlus }),
    ...(params.isLuxPlusMember && { isLuxPlusMember: params.isLuxPlusMember }),
    ...(params.hasLuxPlusPrice && { hasLuxPlusPrice: params.hasLuxPlusPrice }),
    ...(params.isCruiseTour && { isCruiseTour: params.isCruiseTour }),
    ...(params.offerSource && { offerSource: params.offerSource }),
    ...(params.cruiseShips && { cruiseShips }),
    ...(cruiseTypes?.length && { cruiseTypes }),
    ...(cruiseTravellerSearchTypes.length && { travellerTypes: cruiseTravellerSearchTypes }),
  }

  const response = await request
    .post<CruiseSearchFacetListPostResponseWithTravellerTypes, CruiseSearchFacetListPostBodyWithTravellerTypes>(
      '/api/search/cruise/v1/facet/list',
      body,
    )

  const { filtersV2 } = response

  return {
    cruiseLines: filtersV2.cruise_lines.map(item => {
      const customInfo = CRUISE_LINE_CUSTOM_INFO.find(({ code }) => code === item.slug)
      return {
        slug: item.slug,
        label: customInfo?.name ?? item.label,
        originalLabel: item.label,
        count: item.count,
        imageId: item.imageId,
        smallImageId: customInfo?.imageIdSm ?? item.smallImageId,
        order: item.order,
      }
    }),
    shipNames: filtersV2.ship_names,
    cabinTypes: filtersV2.cabin_types,
    cruiseTypes: filtersV2.cruise_types,
    travellerTypes: (filtersV2.traveller_types ?? []).map(facet => ({
      ...facet,
      slug: getCruiseTravellerFilterValue(facet.slug),
    })),
    campaigns: filtersV2.campaigns,
    departurePorts: filtersV2.departure_ports,
    destinationPorts: filtersV2.destination_ports,
    specialOffers: filtersV2.special_offers,
    luxExclusive: filtersV2.lux_exclusive,
    luxPlus: filtersV2.lux_plus,
    luxPlusPrice: filtersV2.lux_plus_price,
    cruiseDurations: filtersV2.cruise_durations,
    cruisePrices: filtersV2.cruise_prices ?? undefined,
    cruiseDates: filtersV2.cruise_dates,
    departureMonths: filtersV2.departure_months,
  }
}

function getCruiseSearchBody(params: App.OfferListFilters, region?: string): CruiseSearchRequestWithTravellerTypes {
  // destinationIds is an array of strings, but it can also be a string with multiple ids separated by ','
  const destinationIds = params.destinationIds?.flatMap(id => id.split(','))
  const departurePlaceIds = params.departureIds?.flatMap(id => id.split(','))
  const departureIds = params.offerDepartureIds?.flatMap(id => id.split(','))
  const durationRange = params.durationRange?.flatMap(duration => duration.split(','))
  const cruiseTypes = params.cruiseTypes?.flatMap(cruiseType => cruiseType.split(','))
  const travellerTypes = params.travellerTypes?.flatMap(travellerType => travellerType.split(','))
  const cruiseTravellerSearchTypes = getCruiseTravellerSearchTypes(travellerTypes)
  const { durationMin, durationMax } = params

  return {
    brand: config.BRAND,
    arrivalPlaceId: params.destinationId,
    departurePlaceId: params.departurePlaceId,
    departureDate: params.departureDate?.substring(0, 7), // gets YYYY-MM
    departureStartDate: params.departureStartDate,
    departureEndDate: params.departureEndDate,
    departureMonths: params.departureMonths?.filter(Boolean),
    cruiseLines: params.cruiseLines,
    rateCodes: params.rateCodes,
    cruiseShips: params.cruiseShips,
    cabinTypes: params.cabinTypes,
    cruiseTypes,
    travellerTypes: cruiseTravellerSearchTypes.length ? cruiseTravellerSearchTypes : undefined,
    // if a 0 is passed, it's meaningless, so get rid of it
    minPrice: params.minPrice || undefined,
    maxPrice: params.maxPrice || undefined,
    sortBy: params.sortBy,
    campaigns: params.campaigns, // Keep as array, not comma-separated
    ...(region && { region: region as CruiseSearchRegion }),
    ...(durationMin && { durationMin }),
    ...(durationMax && { durationMax }),
    ...(durationRange && { durationRange }),
    ...(params.isSpecialOffers && { isSpecialOffers: params.isSpecialOffers }),
    ...(params.isLuxExclusivePromotion && { isLuxExclusivePromotion: params.isLuxExclusivePromotion }),
    ...(params.isLuxPlus && { isLuxPlus: params.isLuxPlus }),
    ...(params.isLuxPlusMember && { isLuxPlusMember: params.isLuxPlusMember }),
    ...(params.hasLuxPlusPrice && { hasLuxPlusPrice: params.hasLuxPlusPrice }),
    ...(params.isCruiseTour && { isCruiseTour: params.isCruiseTour }),
    ...(params.offerSource && { offerSource: params.offerSource }),
    destinationName: params.destinationName,
    departureName: params.departureName,
    destinationPlaceIds: destinationIds,
    destinationNames: params.destinationNames,
    departurePlaceIds,
    departureIds,
    departureNames: params.departureNames,
    priceBy: params.priceBy,
  }
}

export async function getCruiseSearchList(params: App.OfferListFilters, region?: string) {
  const body = getCruiseSearchBody(params, region)
  const response = await request.post<CruiseSearchListPostResponse, CruiseSearchRequestWithTravellerTypes>(
    '/api/search/cruise/v1/list',
    body,
  )

  return (response.result ?? []).map(id => {
    const isFlashOffer = id.startsWith('tour')
    return isFlashOffer ? id : `cruise:${id}`
  })
}

export async function getCruiseSearchCount(params: App.OfferListFilters, region?: string) {
  const body = getCruiseSearchBody(params, region)
  const response = await request.post<App.ApiResponse<number>, CruiseSearchRequestWithTravellerTypes>(
    '/api/search/cruise/v1/count',
    body,
  )
  return response.result
}

function getCruiseTravellerSearchTypes(
  travellerTypes: Array<string> = [],
): Array<CruiseTravellerSearchType> {
  return travellerTypes
    .map(travellerType => CRUISE_TRAVELLER_SEARCH_TYPE_BY_FILTER[travellerType as CruiseTravellerType])
    .filter((travellerType): travellerType is CruiseTravellerSearchType => Boolean(travellerType))
}

function getCruiseTravellerFilterValue(searchType: string): string {
  return CRUISE_TRAVELLER_FILTER_BY_SEARCH_TYPE[searchType as CruiseTravellerSearchType] ?? searchType
}

export async function getCruiseOfferSummaryBySailingId(id: string, regionCode: string) {
  const response = await request.get<
    App.ApiResponse<CruisesContract.OfferSummary>
  >(`${BASE_URI}/offers/${id}`)

  return cruiseOfferMap(response.result, regionCode)
}

export async function getCruiseOffersBySailingId(ids: Array<string>, regionCode: string) {
  return await Promise.all(ids.map(id =>
    getCruiseOfferSummaryBySailingId(id, regionCode),
  ))
}

export async function getCruiseOfferByIdType(id: string, region: string, currencyCode?: string, isSpoofView?: boolean) {
  if (id.startsWith('cruise-details:')) {
    return getCruiseOffer(id, region, currencyCode, isSpoofView)
  }

  return getCruiseOfferSummary(id, region, currencyCode)
}

export async function getCruiseOffer(id: string, region: string, currencyCode?: string, isSpoofView?: boolean) {
  const idWithoutPrefix = id.replace('cruise-details:', '')
  if (!isUuidV4(idWithoutPrefix)) {
    throw new Error('Invalid cruise offer ID')
  }

  const params = qs.stringify({
    currencyCode,
    region,
    isSpoofView,
  })

  const response = await request.get<
    App.ApiResponse<CruisesContract.Offer>
  >(`${BASE_URI}/offers/${idWithoutPrefix}?${params}`)

  return cruiseOfferMap(response.result, region)
}

export async function getCruiseOfferMemberPricing(
  id: string,
  region: string,
  currencyCode?: string,
  hasAccessToMemberPricing?: boolean,
): Promise<App.Cruises.CruiseOfferMemberPricing> {
  const idWithoutPrefix = id.replace('cruise:', '').replace('cruise-details:', '')
  if (!isUuidV4(idWithoutPrefix)) {
    throw new Error('Invalid cruise offer ID')
  }

  const params = qs.stringify({
    currencyCode,
    region,
    hasAccessToMemberPricing,
  })

  const response = await request.get<
    App.ApiResponse<CruiseAPI.CruiseOfferMemberPricingResponse>
  >(`${BASE_URI}/offers/${idWithoutPrefix}/member-price?${params}`, { credentials: 'include' })

  return mapCruiseOfferMemberPricing(response.result)
}

export async function getCruiseOfferSummary(id: string, region: string, currencyCode?: string) {
  const idWithoutPrefix = id.replace('cruise:', '')
  if (!isUuidV4(idWithoutPrefix)) {
    throw new Error('Invalid cruise offer ID')
  }

  const params = qs.stringify({
    currencyCode,
    region,
  })

  const response = await request.get<
    App.ApiResponse<definitions['OFFER_SUMMARY']>
  >(`${BASE_URI}/offers/${idWithoutPrefix}/summary?${params}`)

  return cruiseOfferSummaryMap(response.result, region)
}

export async function getValidCruiseOffer(id: string, region: string, currencyCode?: string) {
  const offer = await getCruiseOffer(id, region, currencyCode)
  const hasValidDepartures = offerHasValidDepartures(offer)

  if (!hasValidDepartures) {
    throw new Error(`No valid departures found for offer ${id}`)
  }

  return offer
}

export async function getCruiseDepartureById(departureId: string) {
  const response = await request.get<
    App.ApiResponse<CruiseAPI.DepartureByIdResponse>
  >(`${BASE_URI}/departures/${departureId}`)

  return cruiseDepartureByIdMap(response.result)
}

// BOOKING FLOW V2
export async function findBookingRateList(params: CruiseAPI.CruiseRateListBody, offer: App.CruiseOffer): Promise<App.Cruises.BookingRateListResponse> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseRateListResponse>, CruiseAPI.CruiseRateListBody>(
    `${BASE_URI}/booking-flow/rate-list`,
    params,
    { credentials: 'include' },
  )

  if (res.errorToRender) return res.errorToRender

  const cabinRateList = cruiseBookingRateListMap(res.result, offer, params.departureId, params.region)
  setCruiseVendorBookingTimer(cabinRateList.cabinHoldTimeout || DEFAULT_CABIN_HOLD_TIMEOUT)

  return cabinRateList
}

export async function findBookingVendorConfiguration(
  params: CruiseAPI.CruiseVendorConfigurationBody,
): Promise<App.Cruises.CruiseVendorConfiguration> {
  const res = await request.post<
    App.ApiResponse<CruiseAPI.CruiseVendorConfigurationResponse>,
    CruiseAPI.CruiseVendorConfigurationBody
  >(
    `${BASE_URI}/booking-flow/vendor-configuration`,
    params,
    { credentials: 'include' },
  )

  if (res.errorToRender) return res.errorToRender

  return cruiseVendorConfigurationMap(res.result)
}

export interface BookingCabinListParams {
  componentIds: Array<string>
  sessionId: string
}

export async function findBookingMultipleCabinList(
  params: Omit<CruiseAPI.CruiseCabinListBody, 'componentIds'>,
  componentIds: Array<Array<string>>,
): Promise<Array<App.Cruises.CruiseBookingCabin>> {
  const componentIdsChunked = split(componentIds, MAX_NUM_OF_PARALLEL_REQUESTS)
  let results: Array<App.Cruises.CruiseBookingCabin> = []
  let requestError: App.ApiCallFailure | undefined

  for (let i = 0; i < componentIdsChunked.length; i++) {
    const resultsSubset = await findBookingCabinListSubset(params, componentIdsChunked[i])
      .catch((error) => {
        // Ignore error, it will be handled later if all cabin-list calls fail.
        requestError = error
        return []
      })

    results = [...results, ...resultsSubset.flat()]
  }

  if (requestError && results.length === 0) {
    // If all cabin-list calls fail, throw the error.

    throw requestError
  }

  return results
}

function findBookingCabinListSubset(
  params: Omit<CruiseAPI.CruiseCabinListBody, 'componentIds'>,
  componentIds: Array<Array<string>>,
): Promise<Array<Array<App.Cruises.CruiseBookingCabin>>> {
  return Promise.all(
    componentIds.map((ids) => findBookingCabinList({
      ...params,
      componentIds: ids,
    })),
  )
}

async function findBookingCabinList(params: CruiseAPI.CruiseCabinListBody): Promise<Array<App.Cruises.CruiseBookingCabin>> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinListResponse>, CruiseAPI.CruiseCabinListBody>(
    `${BASE_URI}/booking-flow/cabin-list`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinListMap(res.result)
}

export async function findBookingRateDetailsList(
  params: CruiseAPI.CruiseRateDetailsListBody,
): Promise<Record<string, App.Cruises.BookingRateDetails>> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseRateDetailsListResponse>, CruiseAPI.CruiseRateDetailsListBody>(
    `${BASE_URI}/booking-flow/rate-code-details-list`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingRateDetailsListMap(res.result)
}

export async function findBookingCabinSelection(
  params: CruiseAPI.CruiseCabinSelectionBody,
): Promise<App.Cruises.CruiseBookingCabinSelection> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinSelectionResponse>, CruiseAPI.CruiseCabinSelectionBody>(
    `${BASE_URI}/booking-flow/cabin-selection`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinSelectionMap(res.result)
}

export async function findBookingCabinPricing(
  params: CruiseAPI.CruiseCabinPricingBody,
): Promise<App.Cruises.CruiseBookingCabinPricing> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinPricingResponse>, CruiseAPI.CruiseCabinPricingBody>(
    `${BASE_URI}/booking-flow/cabin-pricing`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinPricingMap(res.result)
}

export async function findBookingPastPassengerLookup(
  params: CruiseAPI.CruisePastPassengerLookupBody,
): Promise<CruiseAPI.CruisePastPassengerLookupResponse> {
  const res = await request.post<
    App.ApiResponse<CruiseAPI.CruisePastPassengerLookupResponse>,
    CruiseAPI.CruisePastPassengerLookupBody
  >(
    `${BASE_URI}/booking-flow/past-passenger-lookup`,
    params,
    { credentials: 'include' },
  )

  if (res.errorToRender) return res.errorToRender

  return res.result
}

export async function cruiseBookingCabinRelease(
  params: CruiseAPI.CruiseCabinReleaseBody,
) {
  await request.post<App.ApiResponse<CruiseAPI.CruiseCabinReleaseRelease>, CruiseAPI.CruiseCabinReleaseBody>(
    `${BASE_URI}/booking-flow/cabin-release`,
    params,
    { credentials: 'include' },
  )

  return {
    success: true,
  }
}

export async function findBookingCabinDetailsListMultiple(
  params: Omit<CruiseAPI.CruiseCabinDetailsListBody, 'cabinNumbers'>,
  cabinNumberGroups: Array<Array<string>>,
): Promise<App.Cruises.BookingCabinDetailsList> {
  const results = await Promise.all(
    cabinNumberGroups.map((cabinNumbers) => findBookingCabinDetailsList({
      ...params,
      cabinNumbers,
    })),
  )

  return results.reduce((acc, res) => ({ ...acc, ...res }), {})
}

export async function findBookingCabinDetailsList(
  params: CruiseAPI.CruiseCabinDetailsListBody,
): Promise<App.Cruises.BookingCabinDetailsList> {
  const res = await request.post<App.ApiResponse<CruiseAPI.CruiseCabinDetailsListResponse>, CruiseAPI.CruiseCabinDetailsListBody>(
    `${BASE_URI}/booking-flow/cabin-details-list`,
    params,
    { credentials: 'include' },
  )

  return cruiseBookingCabinDetailsListMap(res.result)
}

export async function findCruiseShip(id: string) {
  const response = await request.get<
    App.ApiResponse<CruiseAPI.CruiseShipResponse>
  >(`${BASE_URI}/ships/${id}`)

  return cruiseShipMap(response.result)
}

export function getConsolidatedPaymentSchedule(
  bookingIds: Array<string>,
): Promise<CruiseAPI.CruiseConsolidatedPaymentSchedule> {
  const queryParams = qs.stringify({ bookingIds })

  return request.get<
    App.ApiResponse<CruiseAPI.CruiseConsolidatedPaymentSchedule>
  >(`${BASE_URI}/payment-schedules/consolidated?${queryParams}`, { credentials: 'include' })
    .then((response) => response.result)
}

export async function getCruiseOfferItinerary(offerId: string): Promise<App.Cruises.OfferItineraryV2> {
  const response = await request.get<App.ApiResponse<App.Cruises.OfferItineraryV2>>(
    `${BASE_URI}/offers/${offerId}/itinerary`,
  )
  return response.result
}

export async function getCruiseOfferSnapshot(
  bookingId: string,
): Promise<App.Cruises.CruiseOfferSnapshot> {
  const response = await request.get<
    App.ApiResponse<CruiseAPI.CruiseOfferSnapshot>
  >(`${BASE_URI}/bookings/${bookingId}/snapshot/offer`, { credentials: 'include' })

  return cruiseOfferSnapshotMap(response.result)
}

export async function getQuickModificationBookingDisplay(bookingId: string): Promise<App.Cruises.CruiseQuickModificationDisplay> {
  const response = await request.get<App.ApiResponse<CruiseAPI.CruiseQuickModificationDisplay>>(
    `${BASE_URI}/bookings/${bookingId}/quick-modification/booking-display`,
    { credentials: 'include' },
  )
  return cruiseQuickModificationBookingDisplayMap(response.result)
}
